import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CONTACT_TYPES } from "@/lib/contacts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X, Plus } from "lucide-react";

const EMPTY = { company: "", entity_type: "Empresa", contact_person: "", type: "Proveedor", phone: "", email: "", location: "", website: "", cif_nif: "", notes: "" };

export default function ContactFormModal({ open, onOpenChange, onSaved, contact }) {
  const editing = Boolean(contact);
  const [form, setForm] = useState(EMPTY);
  const [activities, setActivities] = useState([]);
  const [activityInput, setActivityInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(contact
      ? Object.fromEntries(Object.keys(EMPTY).map((k) => [k, contact[k] ?? EMPTY[k]]))
      : EMPTY);
    setActivities(contact?.activities || []);
    setActivityInput("");
    api.get("/contact-activities").then((r) => setSuggestions(r.data)).catch(() => {});
  }, [open, contact]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addActivity = (raw) => {
    const name = (raw || "").trim();
    if (!name) return;
    if (!activities.some((a) => a.toLowerCase() === name.toLowerCase())) {
      setActivities((a) => [...a, name]);
    }
    if (!suggestions.some((s) => s.toLowerCase() === name.toLowerCase())) {
      setSuggestions((s) => [...s, name]);
    }
    setActivityInput("");
  };

  const removeActivity = (name) => setActivities((a) => a.filter((x) => x !== name));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company.trim()) return toast.error("El nombre de empresa es obligatorio.");
    setSaving(true);
    const payload = {
      company: form.company.trim(), entity_type: form.entity_type, contact_person: form.contact_person.trim() || null, type: form.type,
      activities,
      phone: form.phone.trim() || null, email: form.email.trim() || null, location: form.location.trim() || null,
      website: form.website.trim() || null, cif_nif: form.cif_nif.trim() || null, notes: form.notes.trim() || null,
    };
    try {
      const r = editing
        ? await api.put(`/contacts/${contact.contact_id}`, payload)
        : await api.post("/contacts", payload);
      onSaved(r.data, editing);
      onOpenChange(false);
      toast.success(editing ? "Contacto actualizado." : "Contacto añadido al CRM.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar el contacto.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contact-form-modal">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar contacto" : "Nuevo contacto"}</DialogTitle>
          <DialogDescription>Los contactos se usan como proveedores/suministradores en los recursos.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-4">
            <div className="space-y-1.5">
              <Label>Tipo de contacto</Label>
              <Select value={form.entity_type} onValueChange={(v) => set("entity_type", v)}>
                <SelectTrigger data-testid="contact-entity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Empresa" data-testid="contact-entity-option-Empresa">Empresa</SelectItem>
                  <SelectItem value="Persona" data-testid="contact-entity-option-Persona">Persona</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-company">{form.entity_type === "Persona" ? "Nombre y apellidos *" : "Nombre de la empresa *"}</Label>
              <Input id="c-company" data-testid="contact-company-input"
                placeholder={form.entity_type === "Persona" ? "Ej. Juan Pérez López" : "Ej. Cementos del Turia SA"}
                value={form.company} onChange={(e) => set("company", e.target.value)} autoFocus />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {form.entity_type === "Empresa" && (
              <div className="space-y-1.5">
                <Label htmlFor="c-person">Persona de contacto</Label>
                <Input id="c-person" data-testid="contact-person-input" placeholder="Opcional"
                  value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Relación</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="contact-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTACT_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`contact-type-option-${t}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Actividad profesional</Label>
            <p className="text-xs text-muted-foreground -mt-1">Puedes añadir varias. Elige del listado o escribe una nueva.</p>
            {activities.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid="contact-activities-selected">
                {activities.map((a) => (
                  <Badge key={a} variant="secondary" className="gap-1 font-normal pr-1">
                    {a}
                    <button type="button" onClick={() => removeActivity(a)} className="rounded-full hover:text-destructive" aria-label={`Quitar ${a}`} data-testid={`activity-remove-${a}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input data-testid="contact-activity-input" placeholder="Ej. Fontanería"
                value={activityInput}
                onChange={(e) => setActivityInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addActivity(activityInput); } }} />
              <Button type="button" variant="outline" className="rounded-full shrink-0 gap-1" onClick={() => addActivity(activityInput)} data-testid="contact-activity-add">
                <Plus className="h-4 w-4" /> Añadir
              </Button>
            </div>
            {suggestions.filter((s) => !activities.some((a) => a.toLowerCase() === s.toLowerCase()) && (!activityInput || s.toLowerCase().includes(activityInput.toLowerCase()))).length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1.5" data-testid="contact-activity-suggestions">
                {suggestions
                  .filter((s) => !activities.some((a) => a.toLowerCase() === s.toLowerCase()) && (!activityInput || s.toLowerCase().includes(activityInput.toLowerCase())))
                  .slice(0, 12)
                  .map((s) => (
                    <button key={s} type="button" onClick={() => addActivity(s)}
                      className="text-xs rounded-full border border-border bg-background px-2.5 py-1 text-muted-foreground hover:border-primary hover:text-foreground transition-colors"
                      data-testid={`activity-suggestion-${s}`}>
                      + {s}
                    </button>
                  ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="c-phone">Teléfono</Label>
              <Input id="c-phone" data-testid="contact-phone-input" placeholder="Opcional"
                value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" type="email" data-testid="contact-email-input" placeholder="Opcional"
                value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="c-location">Población / Provincia</Label>
              <Input id="c-location" data-testid="contact-location-input" placeholder="Opcional"
                value={form.location} onChange={(e) => set("location", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-cif">CIF / NIF</Label>
              <Input id="c-cif" data-testid="contact-cif-input" placeholder="Opcional"
                value={form.cif_nif} onChange={(e) => set("cif_nif", e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-web">Url / Web</Label>
            <Input id="c-web" data-testid="contact-website-input" placeholder="Opcional (ej. https://…)"
              value={form.website} onChange={(e) => set("website", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-notes">Notas</Label>
            <Textarea id="c-notes" data-testid="contact-notes-input" placeholder="Opcional" rows={2}
              value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="rounded-full" disabled={saving} data-testid="contact-save-button">
              {saving ? "Guardando…" : editing ? "Guardar cambios" : "Añadir contacto"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
